/**
 * Whether a deadline has passed, rendering once more the moment it does.
 * An approved launch whose approval expires while its card is on screen
 * offers the decision again without a reload (TRA-1347).
 */

import { useEffect, useState } from 'react'

// setTimeout fires at once for a longer delay, so a far deadline is
// checked again after this long instead.
const MAX_WAIT_MS = 2 ** 31 - 1

/** A null deadline, or one that does not parse, never passes. */
export function useHasPassed(deadline: string | null): boolean {
  const at = deadline === null ? Number.NaN : Date.parse(deadline)
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    if (Number.isNaN(at)) return
    let timer: ReturnType<typeof setTimeout> | undefined
    // A timer can wake a moment early by the wall clock, so it waits again.
    const wake = (): void => {
      const left = at - Date.now()
      if (left > 0) timer = setTimeout(wake, Math.min(left, MAX_WAIT_MS))
      else setNow(Date.now())
    }
    timer = setTimeout(wake, 0)
    return () => {
      clearTimeout(timer)
    }
  }, [at])
  return now >= at
}
